/**
 * IdResolvePass - Manually resolves multisampled ID targets to single-sampled textures.
 *
 * WebGPU does not support automatic MSAA resolve for integer formats (like rgba8uint).
 * This pass reads sample 0 from the multisampled ID textures and writes to single-sampled
 * textures that can be used for readback and picking operations.
 */

const idResolveShader = /* wgsl */`
@group(0) @binding(0) var idTexMS: texture_multisampled_2d<u32>;
@group(0) @binding(1) var idTexOut: texture_storage_2d<rgba8uint, write>;

@compute @workgroup_size(8, 8)
fn main(@builtin(global_invocation_id) globalId: vec3u) {
    let dims = textureDimensions(idTexMS);
    if (globalId.x >= dims.x || globalId.y >= dims.y) {
        return;
    }

    let value = textureLoad(idTexMS, vec2i(globalId.xy), 0);
    textureStore(idTexOut, vec2i(globalId.xy), value);
}
`;

/** @import { Renderer } from '../Renderer' */

export class IdResolvePass {
  /** @type {Renderer} */
  #renderer;
  /** @type {GPUDevice} */
  #device;

  /** @type {GPUComputePipeline} */
  #pipeline;
  /** @type {GPUBindGroupLayout} */
  #bindGroupLayout;
  /** @type {GPUBindGroup[]} */
  #bindGroups = [];

  /**
   * @param {Renderer} renderer
   */
  constructor(renderer) {
    this.#renderer = renderer;
  }

  init() {
    this.#device = this.#renderer.getDevice();

    this.#bindGroupLayout = this.#device.createBindGroupLayout({
      label: 'id-resolve-bind-group-layout',
      entries: [
      {
        binding: 0,
        visibility: GPUShaderStage.COMPUTE,
        texture: {
          sampleType: 'uint',
          multisampled: true
        }
      },
      {
        binding: 1,
        visibility: GPUShaderStage.COMPUTE,
        storageTexture: {
          access: 'write-only',
          format: 'rgba8uint'
        }
      }]

    });

    const shaderModule = this.#device.createShaderModule({
      label: 'id-resolve-shader',
      code: idResolveShader
    });

    this.#pipeline = this.#device.createComputePipeline({
      label: 'id-resolve-pipeline',
      layout: this.#device.createPipelineLayout({
        bindGroupLayouts: [this.#bindGroupLayout]
      }),
      compute: {
        module: shaderModule,
        entryPoint: 'main'
      }
    });
  }

  /**
   * Updates bind groups when render targets are resized.
   */
  resize() {
    const rt = this.#renderer.getRenderTargets();

    if (rt.getSampleCount() <= 1) {
      this.#bindGroups = [];
      return;
    }

    this.#bindGroups = [];
    const numTargets = rt.getNumIdTargets();
    for (let i = 0; i < numTargets; i++) {
      const msTexture = rt.getIdTarget(i);
      const resolveTexture = rt.getIdTargetForSampling(i);

      if (!msTexture || !resolveTexture) {
        continue;
      }

      const bindGroup = this.#device.createBindGroup({
        label: `id-resolve-bind-group-${i}`,
        layout: this.#bindGroupLayout,
        entries: [
        { binding: 0, resource: msTexture.createView() },
        { binding: 1, resource: resolveTexture.createView() }]

      });

      this.#bindGroups.push(bindGroup);
    }
  }

  /**
   * Encodes the ID resolve pass, copying sample 0 from multisampled ID targets
   * to single-sampled targets for readback. The caller is responsible for
   * submitting the command encoder.
   * @param {GPUCommandEncoder} commandEncoder
   */
  run(commandEncoder) {
    const rt = this.#renderer.getRenderTargets();

    if (rt.getSampleCount() <= 1 || this.#bindGroups.length === 0) {
      return;
    }

    const [width, height] = rt.getTargetSize();
    const workgroupsX = Math.ceil(width / 8);
    const workgroupsY = Math.ceil(height / 8);

    const passEncoder = commandEncoder.beginComputePass({
      label: 'id-resolve-pass'
    });

    passEncoder.setPipeline(this.#pipeline);

    for (let i = 0; i < this.#bindGroups.length; i++) {
      passEncoder.setBindGroup(0, this.#bindGroups[i]);
      passEncoder.dispatchWorkgroups(workgroupsX, workgroupsY);
    }

    passEncoder.end();
  }
}